import { useEffect, useState, type ReactNode } from 'react';
import { t } from '../../i18n/idioma';

/**
 * A pílula padrão dos mecanismos idle (techspec-hud-v3 §18): coleta e caçada entram minimizadas nela.
 * À esquerda a arte e o que o personagem está fazendo (clicar abre a janela); no meio o relógio; à direita
 * o play/pause (só quando o idle pode pausar: hoje, a caçada) e o sair. O sair pede um segundo clique em
 * 3 s, porque leva o personagem embora (Recall) e um clique perdido na pílula não pode custar a coleta.
 */
export function PilulaIdle({
  arte, estado, relogio, pausado, podePausar = false, ocupado = false, abrirDica, sairDica, className, onAbrir, onPausa, onSair
}: {
  /** A arte à esquerda (a ferramenta da coleta); a caçada vai sem. */
  arte?: ReactNode;
  /** O que está fazendo agora ("Minerando", "Caçando · Cemitério de Cove", "Pausado"). */
  estado: string;
  relogio: string;
  pausado?: boolean;
  podePausar?: boolean;
  ocupado?: boolean;
  abrirDica: string;
  sairDica: string;
  className?: string;
  onAbrir(): void;
  onPausa?(): void;
  onSair(): void;
}) {
  const [confirma, setConfirma] = useState(false);
  useEffect(() => {
    if (!confirma) return;
    const id = window.setTimeout(() => setConfirma(false), 3000);
    return () => window.clearTimeout(id);
  }, [confirma]);

  return (
    <div className={`idle-pill${pausado ? ' is-pausado' : ''}${className ? ` ${className}` : ''}`} role="group" aria-label={estado}>
      <button type="button" className="idle-pill__abrir" onClick={onAbrir} title={abrirDica}>
        {arte ? <span className="idle-pill__arte">{arte}</span> : null}
        <span className="ch-pulso"><i />{estado}</span>
        <b>{relogio}</b>
      </button>
      {podePausar && onPausa && (
        <button
          type="button"
          className="idle-pill__b idle-pill__b--pausa"
          aria-pressed={!!pausado}
          disabled={ocupado}
          title={t(pausado ? 'idle.retomar' : 'idle.pausar')}
          aria-label={t(pausado ? 'idle.retomar' : 'idle.pausar')}
          onClick={onPausa}
        >
          {pausado ? (
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M5 3.5v9l7.5-4.5z" /></svg>
          ) : (
            <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M4.5 3.5h2.5v9H4.5zM9 3.5h2.5v9H9z" /></svg>
          )}
        </button>
      )}
      <button
        type="button"
        className={`idle-pill__b idle-pill__b--sair${confirma ? ' is-confirma' : ''}`}
        disabled={ocupado}
        title={confirma ? t('idle.sairConfirma') : sairDica}
        aria-label={confirma ? t('idle.sairConfirma') : sairDica}
        onClick={() => {
          if (!confirma) { setConfirma(true); return; }
          setConfirma(false);
          onSair();
        }}
      >
        {confirma ? <span>{t('idle.sair')}</span> : (
          <svg viewBox="0 0 16 16" aria-hidden="true"><path d="M6 2.5H3.5v11H6M10 5l3 3-3 3M13 8H6.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
        )}
      </button>
    </div>
  );
}
